import type { StripeCardElement } from "@stripe/stripe-js";



import { useBillStore } from "@/data/stores/bill";
import { useCartStore } from "@/data/stores/cart";



import { Factory } from "@/data/dao/Factory";
import { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CustomerDao } from "@/data/dao/service/CustomerDao";
import type { ISelectedItem } from "@/data/model/Bill";
import type { ICreditCard } from "@/data/model/CreditCard";
import type { IPaymentIntent, IPaymentIntentConfirmHandle, IPaymentMethod } from "@/data/model/Customer";
import { PaymentFor, PaymentProvider, SchedulingMode, ServiceType, UiPaymentState, WorldlineTerminalPaymentStatus } from "@/data/model/Enums";
import type { IService } from "@/data/model/Service";
import { config, exceptionReasons, exceptionTypes } from "@/data/util/Config";
import { DateTime } from "@/data/util/DateTime";
import { Format } from "@/data/util/Format";
import { JavascriptLoader } from "@/data/util/JavascriptLoader";
import { Signal } from "@/data/util/Signal";



import { translations as trans, translations } from "@/shared/localization/translations";
import { logError } from "@/shared/logging/errors";
import { DeviceFingerprintProvider } from "@/shared/services/DeviceFingerprintProvider";
import { sendWorldlineTransactionToAuthorize } from "@/shared/utils/androidNativeUtils";
import { toMoneyWithSign } from "@/shared/utils/format";



import { Popup } from "@/components/dialog/Popup";
import EnterContactDataForm from "@/components/pay/EnterContactDataForm.vue";
import EnterDeliveryAddressForm from "@/components/pay/EnterDeliveryAddressForm.vue";



import { operatorEvents } from "@/operator/events/operatorEvents";


export interface IStripeFormValues {
    cardHolder: string;
    cardnoElement: StripeCardElement;
}

export type PayRequest = {
    selectedService: IService;
    cartHash: string | null;
    consumption: number;
    totalDiscount: number;
    tip: number;
    force?: boolean;
    selectedItems?: ISelectedItem[];
    onClickBackBtn?: () => void;
    onGoBackToFulfillmentTimeView?: () => void;
};

export class PayContext {
    public onPaymentAccepted?: { (transactionId: string, paymentFor: PaymentFor) };
    public onCartEmptyGoback?: { () };

    public state: UiPaymentState;

    public defaultTipPercent: number;

    public consumption: number;
    public tipPercent?: number;
    public tipAmount: number;
    public fees: number;
    public total: number;

    public intent?: IPaymentIntent;
    public selectedPaymentMethod?: IPaymentMethod;

    public onAmountChanged: Signal;
    public onStateChanged: Signal;
    public onIntentChanged: Signal;

    public onCheckoutPayLaterCartSuccess: Signal;
    public onCheckoutPayLaterCartError: Signal;

    public onCheckoutPayImmediatellyCartSuccess: Signal;
    public onCheckoutPayImmediatellyCartError: Signal;

    private selectedCreditCard?: ICreditCard;
    private paymentInProgress = false;
    private walleeHandler: any;
    private stripeHandler?: { (): IStripeFormValues };

    private customerDao: CustomerDao;
    private defaultTip = 0.05;

    constructor(defaultTipPercent?: number) {
        // todo: verify default tip
        this.defaultTipPercent = defaultTipPercent == null ? this.defaultTip : defaultTipPercent / 100;
        this.consumption = 0;
        this.total = 0;
        this.fees = 0;
        this.tipPercent = this.defaultTipPercent;
        this.tipAmount = 0;
        this.state = UiPaymentState.Preparing;

        this.onAmountChanged = new Signal();
        this.onStateChanged = new Signal();
        this.onIntentChanged = new Signal();

        this.onCheckoutPayLaterCartSuccess = new Signal();
        this.onCheckoutPayLaterCartError = new Signal();

        this.onCheckoutPayImmediatellyCartSuccess = new Signal();
        this.onCheckoutPayImmediatellyCartError = new Signal();

        this.customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    }

    public async prepareForCheckout(
        selectedService: IService,
        cartHash?: string,
        billId?: string,
        tip?: number
    ): Promise<void> {
        this.selectedPaymentMethod = undefined;
        this.selectedCreditCard = undefined;
        this.state = UiPaymentState.Preparing;
        this.onStateChanged.dispatch();

        const isPayService = this.isPayService(selectedService);
        if (isPayService) {
            try {
                const intent = await this.customerDao.prepareBillForCheckout({
                    payServiceId: selectedService.id,
                    billId: billId!,
                    tip: tip!,
                });
                this.intent = intent;

                this.onIntentChanged.dispatch(intent);
                this.checkState();
                return;
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                if (e.type === exceptionTypes.billIsEmptyException) {
                    if (this.onCartEmptyGoback !== undefined) {
                        this.onCartEmptyGoback();
                    }
                } else if (e.type === exceptionTypes.billNoSelectedItemsException) {
                    // do nothing
                } else {
                    Popup.show({ message: e.message });
                }
                throw ex;
            }
        } else {
            if (cartHash == null) throw "Cart hash is null";

            try {
                const intent = await this.customerDao.prepareCartForCheckout({
                    cartHash: cartHash,
                    orderServiceId: selectedService.id,
                    tip: tip!,
                });
                this.intent = intent;

                this.onIntentChanged.dispatch(intent);
                this.checkState();
                return;
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                if (e.type === exceptionTypes.cartIsEmptyException) {
                    if (this.onCartEmptyGoback !== undefined) {
                        this.onCartEmptyGoback();
                    }
                } else {
                    Popup.show({ message: e.message });
                }
                throw ex;
            }
        }
    }

    public isPayLater(): boolean {
        return this.selectedPaymentMethod?.id === config.payLaterPaymentMethod;
    }

    public async pay(request: PayRequest) {
        if (this.selectedPaymentMethod == null) throw "No payment method selected";

        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (this.isPayLater()) {
            this.state = UiPaymentState.Ordering;
        } else {
            this.state = UiPaymentState.Paying;
        }

        this.paymentInProgress = true;
        this.onStateChanged.dispatch();

        const isPayService = this.isPayService(request.selectedService);
        if (isPayService) {
            // update consumption and tip - if they were not set before
            request.consumption = request.consumption ?? 0;
            request.tip = request.tip ?? 0;
            request.selectedItems = request.selectedItems ?? [];

            const billStore = useBillStore();
            const selectedBill = billStore.currentBill;
            if (selectedBill == null) {
                throw "No selected bill";
            }

            try {
                const intentConfirm = await this.customerDao.checkoutBill({
                    creditCardId: this.selectedCreditCard === undefined ? undefined : this.selectedCreditCard.id,
                    tip: request.tip,
                    consumption: request.consumption,
                    totalDiscount: request.totalDiscount,
                    billId: selectedBill?.id,
                    force: request.force === undefined ? false : request.force,
                    payServiceId: request.selectedService.id,
                    paymentMethod: this.selectedPaymentMethod.id,
                    paymentMethodMode: this.selectedPaymentMethod.mode,
                    paymentProvider: this.selectedPaymentMethod.provider,
                    selectedItems: request.selectedItems,
                    socketConnectionId: this.getSocketConnectionId(),
                    deviceFingerprint: await DeviceFingerprintProvider.getFingerprint(),
                });

                if (intentConfirm?.wasPaymentCoveredByDiscounts === true) {
                    this.paymentInProgress = false;
                } else {
                    if (
                        selectedPaymentMethod.provider === PaymentProvider.Wallee ||
                        selectedPaymentMethod.provider === PaymentProvider.PostFinance
                    ) {
                        await this.onCheckoutBillWithWallee(intentConfirm as any);
                    } else if (selectedPaymentMethod.provider === PaymentProvider.Worldline) {
                        await this.onCheckoutWithWorldline(intentConfirm!);
                    } else if (selectedPaymentMethod.provider === PaymentProvider.Stripe) {
                        if (intentConfirm == null) {
                            this.paymentInProgress = false;
                            return;
                        }

                        //stripe needs additional confirmation handling
                        if (!intentConfirm.isAlreadyConfirmed) {
                            let paymentMethod = undefined as any; // todo: stripe ConfirmCardPaymentData
                            if (this.selectedCreditCard === undefined && this.stripeHandler !== undefined) {
                                const vals = this.stripeHandler();
                                if (vals !== undefined) {
                                    paymentMethod = {
                                        payment_method: {
                                            billing_details: {
                                                name: vals.cardHolder,
                                            },
                                            card: vals.cardnoElement,
                                        },
                                        save_payment_method: true,
                                        setup_future_usage: "off_session",
                                    };
                                }
                            }

                            try {
                                const s = await JavascriptLoader.requireStripe();
                                const a = await s.confirmCardPayment(intentConfirm.intentSecret, paymentMethod);

                                if (a.error === undefined) {
                                    if (this.onPaymentAccepted !== undefined)
                                        this.onPaymentAccepted(intentConfirm.intentId, PaymentFor.Bill);

                                    this.paymentInProgress = false;
                                } else {
                                    if (a.error.code === "card_declined")
                                        Popup.show({ message: trans.pay.failedPaymentInvalidCard });
                                    else Popup.show({ message: trans.pay.failedPaymentUnknownReason });

                                    this.paymentInProgress = false;
                                    this.checkState();

                                    if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                                }
                            } catch (ex) {
                                logError(ex);
                                Popup.show({ message: trans.pay.failedPaymentUnknownReason });
                                this.paymentInProgress = false;
                                this.checkState();

                                if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                            }
                        } else {
                            if (this.onPaymentAccepted !== undefined)
                                this.onPaymentAccepted(intentConfirm.intentId, PaymentFor.Bill);

                            this.paymentInProgress = false;
                        }
                    } else {
                        this.paymentInProgress = false;
                    }
                }

                if (intentConfirm?.isWalleePerformTransactionSucceeded === false) {
                    throw new DaoError("wallee_perform_transaction_failed", "Wallee transaction failed.");
                }
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                this.onCheckoutBillError(e, request);
            }
        } else {
            if (request.cartHash == null) throw "Cart hash is null";

            const billStore = useBillStore();
            const currentBill = billStore.currentBill;
            request.tip = request.tip ?? 0;

            try {
                const intentConfirmHandle = await this.customerDao.checkoutCart({
                    billId: currentBill?.id,
                    creditCardId: this.selectedCreditCard === undefined ? undefined : this.selectedCreditCard.id,
                    cartHash: request.cartHash,
                    tip: request.tip,
                    totalDiscount: request.totalDiscount,
                    consumption: request.consumption,
                    force: request.force === undefined ? false : request.force,
                    orderServiceId: request.selectedService.id,
                    paymentMethod: this.selectedPaymentMethod.id,
                    paymentProvider: this.selectedPaymentMethod.provider,
                    socketConnectionId: this.getSocketConnectionId(),
                    deviceFingerprint: await DeviceFingerprintProvider.getFingerprint(),
                });
                this.paymentInProgress = true;

                if (intentConfirmHandle?.wasPaymentCoveredByDiscounts === true) {
                    this.paymentInProgress = false;
                } else {
                    if (
                        selectedPaymentMethod.provider === PaymentProvider.Wallee ||
                        selectedPaymentMethod.provider === PaymentProvider.PostFinance
                    ) {
                        await this.onCheckoutCartWithWallee(intentConfirmHandle as any);
                    } else if (selectedPaymentMethod.provider === PaymentProvider.Stripe) {
                        await this.checkoutCartWithStripe(intentConfirmHandle as any);
                    } else if (selectedPaymentMethod.provider === PaymentProvider.Worldline) {
                        await this.onCheckoutWithWorldline(intentConfirmHandle!);
                    } else {
                        this.paymentInProgress = false;
                    }
                }

                if (intentConfirmHandle?.isWalleePerformTransactionSucceeded === false) {
                    throw new DaoError("wallee_perform_transaction_failed", "Wallee transaction failed.");
                }

                if (this.isPayLater()) {
                    this.onCheckoutPayLaterCartSuccess.dispatch();
                } else {
                    this.onCheckoutPayImmediatellyCartSuccess.dispatch();
                }
            } catch (ex) {
                logError(ex);
                const e = ex as DaoError;
                this.onCheckoutCartError(e, request);
                if (this.isPayLater()) {
                    this.onCheckoutPayLaterCartError.dispatch();
                } else {
                    this.onCheckoutPayImmediatellyCartError.dispatch();
                }
            }
        }
    }

    private async onCheckoutWithWorldline(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.worldlineTransactionToAuthorize != null) {
            sendWorldlineTransactionToAuthorize(paymentIntentConfirmHandle.worldlineTransactionToAuthorize, paymentIntentConfirmHandle.operatorPaymentUrl);
            return;
        }

        // Terminal API: the server has already run the payment on the terminal, like PayTec, so there is nothing to
        // authorize on the device
        this.paymentInProgress = false;
    }

    private resetCartIfPossible() {
        const cart = useCartStore();
        cart.resetCart();
    }

    private async checkoutCartWithStripe(e: IPaymentIntentConfirmHandle): Promise<void> {
        //stripe needs additional confirmation handling
        if (e == null) {
            this.paymentInProgress = false;
            return;
        }

        if (!e.isAlreadyConfirmed) {
            this.resetCartIfPossible();
            let paymentMethod = undefined as any; //todo: stripe Card whatever
            if (this.selectedCreditCard === undefined && this.stripeHandler !== undefined) {
                const vals = this.stripeHandler();
                if (vals !== undefined) {
                    paymentMethod = {
                        payment_method: {
                            billing_details: {
                                name: vals.cardHolder,
                            },
                            card: vals.cardnoElement,
                        },
                        save_payment_method: true,
                        setup_future_usage: "off_session",
                    };
                }
            }

            try {
                const s = await JavascriptLoader.requireStripe();
                const a = await s.confirmCardPayment(e.intentSecret, paymentMethod);

                if (a.error === undefined) {
                    if (this.onPaymentAccepted !== undefined) this.onPaymentAccepted(e.intentId, PaymentFor.Order);

                    this.paymentInProgress = false;
                } else {
                    if (a.error.code === "card_declined") Popup.show({ message: trans.pay.failedPaymentInvalidCard });
                    else Popup.show({ message: trans.pay.failedPaymentUnknownReason });

                    this.paymentInProgress = false;
                    this.checkState();

                    if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
                }
            } catch (ex) {
                logError(ex);
                Popup.show({ message: trans.pay.failedPaymentUnknownReason });
                this.paymentInProgress = false;
                this.checkState();

                if (this.onCartEmptyGoback !== undefined) this.onCartEmptyGoback();
            }
        } else {
            if (this.onPaymentAccepted !== undefined) this.onPaymentAccepted(e.intentId, PaymentFor.Order);

            this.paymentInProgress = false;
        }
    }

    private onCheckoutBillError(e: DaoError, request: PayRequest) {
        this.paymentInProgress = false;
        logError(e);

        if (e.type === exceptionTypes.billPaymentsInProgressException) {
            // Show the popup
            Popup.show({
                uniqueId: "pmtconfirm",
                message: trans.pay.failedBillPaymentInProgress,
                confirmHandler: () => {
                    if (request.onClickBackBtn) {
                        request.onClickBackBtn();
                    }
                },
                confirmButton: trans.general.ok,
            });
        } else if (
            e.type === exceptionTypes.billInvalidSelectionException ||
            e.type === exceptionTypes.billIsEmptyException ||
            e.type === exceptionTypes.billNoSelectedItemsException
        ) {
            Popup.show({
                uniqueId: "pmtconfirm",
                message: trans.pay.failedBillInvalidSelection,
                confirmButton: trans.general.ok,
                confirmHandler: () => {
                    operatorEvents.payContext.invalidSelectionsPopupViewed.dispatch();
                    if (request.onClickBackBtn) {
                        request.onClickBackBtn();
                    }
                },
            });
        } else if (e.type === exceptionTypes.paymentInProgressException) {
            if (e.additionalData.reason === "payment_in_progress") {
                Popup.show({
                    uniqueId: "pmtconfirm",
                    message: trans.pay.paymentInProgressWarning,
                    confirmHandler: () => {
                        this.pay({
                            ...request,
                            force: true,
                        });
                    },
                    confirmButton: trans.pay.btnOrderAgain,
                    cancelButton: trans.general.cancel,
                    highlightConfirm: true,
                });
            } else if (e.additionalData.reason === "payment_recently_done") {
                Popup.show({
                    uniqueId: "pmtconfirm",
                    message: trans.pay.paymentRecentlyDoneWarning,
                    confirmHandler: () => {
                        this.pay({
                            ...request,
                            force: true,
                        });
                    },
                    confirmButton: trans.pay.btnOrderAgain,
                    cancelButton: trans.general.cancel,
                    highlightConfirm: true,
                });
            } else {
                Popup.show({ message: trans.pay.failedPaymentInProgress });
            }

            this.checkState();
        } else if (e.type === exceptionTypes.stripeException) {
            Popup.show({ message: trans.pay.failedPaymentUnknownReason + " " + e.message });
            this.checkState();
        } else if (e.type === exceptionTypes.walleePaymentMethodNotAvailableException) {
            Popup.show({ message: trans.pay.walleePaymentNotAvailable });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.outOfStockException) {
            let itemList = "<ul>";
            for (const i in e.additionalData.remaining)
                itemList +=
                    "<li>" +
                    Format.htmlEscape(e.additionalData.remaining[i].articleName) +
                    ": " +
                    e.additionalData.remaining[i].remaining +
                    "</li>";

            itemList += "</ul>";

            const msg = trans.pay.failedOutOfStock.replace("{0}", itemList);
            Popup.show({ message: msg });
            this.checkState();
        } else if (
            e.type === exceptionTypes.cartTotalDiscountMismatchException ||
            e.type === exceptionTypes.billTotalDiscountMismatchException
        ) {
            Popup.show({
                uniqueId: "pmtconfirm",
                message: trans.pay.totalDiscountMismatchError,
                confirmButton: trans.general.ok,
                confirmHandler: () => {
                    operatorEvents.payContext.billTotalDiscountMismatchPopupViewed.dispatch();
                },
            });
            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
        } else if (e.type == exceptionTypes.billInTransferModeException) {
            Popup.show({ message: trans.pay.billInTransferMode });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.worldlineTerminalNotConnectedException) {
            Popup.show({ message: trans.pay.worldlineTerminalNotConnected.replace("{0}", Format.htmlEscape(e.additionalData?.terminalId) ?? "") });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.worldlinePaymentCancelledOnTerminalException) {
            Popup.show({ message: trans.pay.failedPaymentUnknownReason });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.worldlinePaymentOutcomeUnknownException) {
            this.showWorldlinePaymentOutcomeUnconfirmed(
                trans.pay.worldlinePaymentOutcomeUnconfirmed,
                e.additionalData.externalPaymentId,
                () => {}
            );
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.paymentOutcomeUnknownException) {
            Popup.show({ message: trans.pay.failedPaymentOutcomeUnknown });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (e.type === exceptionTypes.approvedPaymentNotAcceptedException) {
            Popup.show({ message: trans.pay.approvedPaymentNotAccepted });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else {
            Popup.show({ message: this.worldlineErrorMessage(e) || trans.pay.failedPaymentUnknownReason });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        }
    }

    // The reason a Worldline Terminal API payment failed (e.g. an invalid token) is the message of the server's error.
    // Empty when the payment is not a Worldline one or the error has no message
    private worldlineErrorMessage(e: DaoError): string {
        if (this.selectedPaymentMethod?.provider !== PaymentProvider.Worldline || !e.message) return "";

        // the popup renders its message as html
        const escapedMessage = e.message.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        return escapedMessage.replace(/\r?\n/g, "<br>");
    }

    // The Worldline terminal could not tell whether the payment went through. The waiter can ask it again about that
    // transaction. onApproved does what a successful checkout does, in case the terminal says the payment went through after all.
    private showWorldlinePaymentOutcomeUnconfirmed(message: string, externalPaymentId: string, onApproved: () => void) {
        Popup.show({
            message,
            confirmButton: trans.pay.updatePaymentStatus,
            confirmHandler: () => this.updateWorldlineTerminalPaymentStatus(externalPaymentId, onApproved),
            cancelButton: trans.general.cancel,
            highlightConfirm: true,
        });
    }

    private async updateWorldlineTerminalPaymentStatus(externalPaymentId: string, onApproved: () => void) {
        try {
            const result = await this.customerDao.updateWorldlineTerminalPaymentStatus(externalPaymentId);

            if (result.status === WorldlineTerminalPaymentStatus.Approved) {
                Popup.show({ message: trans.pay.worldlineStatusApproved });
                this.checkState(true);
                onApproved();
            } else if (result.status === WorldlineTerminalPaymentStatus.Declined) {
                Popup.show({ message: trans.pay.worldlineStatusDeclined });
            } else {
                this.showWorldlinePaymentOutcomeUnconfirmed(trans.pay.worldlinePaymentOutcomeUnconfirmed, externalPaymentId, onApproved);
            }
        } catch (ex) {
            logError(ex);

            if ((ex as DaoError).type === exceptionTypes.approvedPaymentNotAcceptedException) {
                Popup.show({ message: trans.pay.approvedPaymentNotAccepted });
            } else {
                // the update itself failed, so the outcome is still unconfirmed
                this.showWorldlinePaymentOutcomeUnconfirmed(trans.pay.worldlinePaymentOutcomeUnconfirmed, externalPaymentId, onApproved);
            }
        }
    }

    private onCheckoutCartError(error: DaoError, request: PayRequest) {
        this.paymentInProgress = false;
        logError(error);

        if (error.type == exceptionTypes.deliveryAddressNotInScopeOfAnyDeliveryAreaException) {
            Popup.show({
                message: trans.deliveryAddress.deliveryAddressNotInScopeError
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        }
        else if (error.type == exceptionTypes.orderConsumptionLowerThanDeliveryMinimumOrderSize) {
            const minimumOrderSize =  error.additionalData.minimumOrderSize;

            Popup.show({
                message: trans.deliveryAddress.minimumOrderSizeError.replace("{0}", toMoneyWithSign(minimumOrderSize)),
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        }
        else if (error.type === exceptionTypes.totalUnitsPerOrderLowerThanMinimumException) {
            const minimumTotalUnitsPerOrder = parseInt(error.additionalData.minimumTotalUnitsPerOrder);
            const currentTotalUnits = parseInt(error.additionalData.currentTotalUnits ?? 0);

            Popup.show({
                message: translations.cart.minimumTotalUnitsPerOrderNotReached.replace(
                    "{0}", (minimumTotalUnitsPerOrder - currentTotalUnits) + ""
                ),
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        }
        else if (error.type === exceptionTypes.totalUnitsPerOrderGreaterThanMaximumException) {
            const maximumTotalUnitsPerOrder = error.additionalData.maximumTotalUnitsPerOrder;

            Popup.show({
                message: translations.cart.maximumTotalUnitsPerOrderExceeded.replace("{0}", maximumTotalUnitsPerOrder + ""),
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        }
        else if (error.type === exceptionTypes.paymentInProgressException) {
            if (error.additionalData.reason === "payment_in_progress") {
                Popup.show({
                    uniqueId: "pmtconfirm",
                    message: trans.pay.paymentInProgressWarning,
                    confirmHandler: () => {
                        this.pay({
                            ...request,
                            force: true,
                        });
                    },
                    confirmButton: trans.pay.btnOrderAgain,
                    cancelButton: trans.general.cancel,
                    highlightConfirm: true,
                });
            } else if (error.additionalData.reason === "payment_recently_done") {
                Popup.show({
                    uniqueId: "pmtconfirm",
                    message: trans.pay.paymentRecentlyDoneWarning,
                    confirmHandler: () => {
                        this.pay({
                            ...request,
                            force: true,
                        });
                    },
                    confirmButton: trans.pay.btnOrderAgain,
                    cancelButton: trans.general.cancel,
                    highlightConfirm: true,
                });
            } else Popup.show({ message: trans.pay.failedPaymentInProgress });

            this.checkState();
        } else if (error.type === exceptionTypes.stripeException) {
            Popup.show({ message: trans.pay.failedPaymentUnknownReason + " " + error.message });
            this.checkState();
        } else if (error.type === exceptionTypes.walleePaymentMethodNotAvailableException) {
            Popup.show({ message: trans.pay.walleePaymentNotAvailable });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.outOfStockException) {
            let itemList = "<ul>";
            for (const i in error.additionalData.remaining)
                itemList +=
                    "<li>" +
                    Format.htmlEscape(error.additionalData.remaining[i].articleName) +
                    ": " +
                    error.additionalData.remaining[i].remaining +
                    "</li>";

            itemList += "</ul>";

            const msg = trans.pay.failedOutOfStock.replace("{0}", itemList);
            Popup.show({ message: msg });
            this.checkState();
        } else if (error.type == exceptionTypes.offerExpiredException) {
            const reason = error.additionalData.reason;
            switch (reason) {
                case exceptionReasons.articlePriceChanged:
                case exceptionReasons.articleNotAvailable:
                    Popup.show({
                        message: trans.pay.cartChangedAndPleaseWaitMessage,
                        confirmHandler: () => {
                            if (request.onClickBackBtn) {
                                request.onClickBackBtn();
                            }
                        },
                    });
                    this.checkState();
                    break;
                default:
                    Popup.show({ message: trans.pay.failedPaymentUnknownReason });
                    this.state = UiPaymentState.Failed;
                    this.onStateChanged.dispatch();
                    break;
            }
        } else if (error.type === exceptionTypes.identityNotSignedUpException) {
            Popup.show({ message: trans.pay.failedIdentityNotSignedUp });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.endUserNotSignedUpException) {
            Popup.show({ message: trans.pay.failedEndUserNotSignedUp });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.moreThanOneReservationInCartException) {
            Popup.show({ message: translations.pay.onlyOneReservationCanBeMadeAtTheTime });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.endUserEmailNotConfirmedException) {
            Popup.show({ message: trans.pay.failedEndUserEmailNotConfirmed });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.emailNotConfirmedException) {
            Popup.show({ message: trans.pay.failedEmailNotConfirmed });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.noFulfillmentSlotsAvailableException) {
            Popup.show({
                message: trans.pay.noFulfillmentSlotAvailable,
                confirmHandler: () => {
                    if (request.onGoBackToFulfillmentTimeView) {
                        request.onGoBackToFulfillmentTimeView();
                    }
                },
            });

            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.newFulfillmentTimeAvailableException) {
            const newAvailableFulfillmentTime = new Date(error.additionalData.newAvailableFulfillmentTimeUtc);
            const timeMode = error.additionalData.timeMode as SchedulingMode;

            this.showNewFulfillmentSlotAvailablePopup(
                false,
                newAvailableFulfillmentTime,
                timeMode,
                request.onClickBackBtn,
                () => {
                    this.pay({
                        ...request,
                        force: true,
                    });
                },
                request.onGoBackToFulfillmentTimeView
            );
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.newFulfillmentDateTimeAvailableException) {
            const newAvailableFulfillmentTime = new Date(error.additionalData.newAvailableFulfillmentTimeUtc);
            const timeMode = error.additionalData.timeMode as SchedulingMode;

            this.showNewFulfillmentSlotAvailablePopup(
                true,
                newAvailableFulfillmentTime,
                timeMode,
                request.onClickBackBtn,
                () => {
                    this.pay({
                        ...request,
                        force: true,
                    });
                },
                request.onGoBackToFulfillmentTimeView
            );

            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.requiredContactDataIsMissingException) {
            const popup = Popup.show({
                title: trans.enterContactDataPopover.title,
                component: EnterContactDataForm,
                componentProps: {
                    hide: () => {
                        popup?.close();
                    },
                },
                showConfirmButton: false,
                closePopupOnOverlayClick: true,
                showCloseButton: true,
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        }else if (error.type === exceptionTypes.deliveryAddressIsMissingException) {
            const popup = Popup.show({
                title: trans.deliveryAddress.enterDeliveryAddress,
                component: EnterDeliveryAddressForm,
                componentProps: {
                    hide: () => {
                        popup?.close();
                    },
                },
                showConfirmButton: false,
                closePopupOnOverlayClick: true,
                showCloseButton: true,
            });

            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
            return;
        } else if (error.type === exceptionTypes.vouchersMustCoverWholePayLaterOrderException) {
            Popup.show({ message: trans.pay.vouchersMustCoverWholePayLaterOrder });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.discountsMustCoverWholePayLaterOrderException) {
            Popup.show({ message: trans.pay.discountsMustCoverWholePayLaterOrder });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (
            error.type === exceptionTypes.cartTotalDiscountMismatchException ||
            error.type === exceptionTypes.billTotalDiscountMismatchException
        ) {
            Popup.show({
                uniqueId: "pmtconfirm",
                message: trans.pay.totalDiscountMismatchError,
                confirmButton: trans.general.ok,
                confirmHandler: () => {
                    operatorEvents.payContext.cartTotalDiscountMismatchPopupViewed.dispatch();
                },
            });
            this.state = UiPaymentState.ReadyToPay;
            this.onStateChanged.dispatch();
        } else if (error.type == exceptionTypes.tableReservationNoAvailabilityException) {
            Popup.show({
                message: trans.pay.noFulfillmentSlotAvailable,
                confirmHandler: () => {
                    if (request.onGoBackToFulfillmentTimeView) {
                        request.onGoBackToFulfillmentTimeView();
                    }
                },
            });

            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.worldlineTerminalNotConnectedException) {
            Popup.show({ message: trans.pay.worldlineTerminalNotConnected.replace("{0}", Format.htmlEscape(error.additionalData?.terminalId) ?? "") });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.worldlinePaymentCancelledOnTerminalException) {
            Popup.show({ message: trans.pay.failedPaymentUnknownReason });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.worldlinePaymentOutcomeUnknownException) {
            this.showWorldlinePaymentOutcomeUnconfirmed(
                trans.pay.worldlinePaymentOutcomeUnconfirmed,
                error.additionalData.externalPaymentId,
                () => {
                    if (this.isPayLater()) {
                        this.onCheckoutPayLaterCartSuccess.dispatch();
                    } else {
                        this.onCheckoutPayImmediatellyCartSuccess.dispatch();
                    }
                }
            );
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.paymentOutcomeUnknownException) {
            Popup.show({ message: trans.pay.failedPaymentOutcomeUnknown });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else if (error.type === exceptionTypes.approvedPaymentNotAcceptedException) {
            Popup.show({ message: trans.pay.approvedPaymentNotAccepted });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        } else {
            Popup.show({ message: this.worldlineErrorMessage(error) || trans.pay.failedPaymentUnknownReason });
            this.state = UiPaymentState.Failed;
            this.onStateChanged.dispatch();
        }
    }

    private showNewFulfillmentSlotAvailablePopup(
        includeDate: boolean,
        newAvailableFulfillmentTime: Date,
        timeMode: SchedulingMode,
        onClickBackBtn?: () => void,
        confirmHandler?: () => void,
        onGoBackToFulfillmentTimeView?: () => void
    ) {
        const message =
            timeMode === SchedulingMode.Time
                ? trans.pay.newFulfillmentSlotAvailable
                      .replace("{0}", trans.pay.scheduledTime)
                      .replace(
                          "{1}",
                          includeDate
                              ? DateTime.toDateTimeString(newAvailableFulfillmentTime)
                              : DateTime.toTimeString(newAvailableFulfillmentTime)
                      )
                : trans.pay.newFulfillmentSlotAvailable
                      .replace("{0}", trans.enums.fulfillmentTimeMode[SchedulingMode[timeMode]])
                      .replace(
                          "{1}",
                          trans.pay.approximately +
                              " " +
                              (includeDate
                                  ? DateTime.toDateTimeString(newAvailableFulfillmentTime)
                                  : DateTime.toTimeString(newAvailableFulfillmentTime))
                      );

        Popup.show({
            message: message,
            confirmHandler: () => {
                if (confirmHandler) {
                    confirmHandler();
                }
            },
            confirmButton: trans.general.accept,
            cancelButton: trans.general.cancel,
            confirmBtnOnTheLeft: false,
            cancelHandler: () => {
                if (onGoBackToFulfillmentTimeView) {
                    onGoBackToFulfillmentTimeView();
                }
            },
        });
    }

    private async onCheckoutBillWithWallee(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.isPaymentDevice) {
            return;
        }

        if (this.selectedPaymentMethod == null) throw "SelectedPaymentMethod is null";

        if (!config.preventPaymentProvider) {
            try {
                await JavascriptLoader.load(paymentIntentConfirmHandle.walleeJavascriptUrl!, "wallee");
                this.walleeHandler = window["IframeCheckoutHandler"](
                    paymentIntentConfirmHandle.walleeSelectedPaymentId
                );
                this.walleeHandler.submit();
            } catch (ex) {
                logError(ex);
                Popup.show({ message: trans.pay.failedPaymentUnknownReason + " " + (ex as any).toString() });
            }
        }
    }

    private async onCheckoutCartWithWallee(paymentIntentConfirmHandle: IPaymentIntentConfirmHandle) {
        if (paymentIntentConfirmHandle.isPaymentDevice) {
            return;
        }

        if (paymentIntentConfirmHandle.walleeJavascriptUrl == null) {
            throw "walleeJavascriptUrl is null";
        }

        const selectedPaymentMethod = this.selectedPaymentMethod;
        if (selectedPaymentMethod == null) {
            throw "selectedPaymentMethod is null";
        }

        if (!config.preventPaymentProvider) {
            try {
                await JavascriptLoader.load(paymentIntentConfirmHandle.walleeJavascriptUrl, "wallee");

                this.walleeHandler = window["IframeCheckoutHandler"](
                    paymentIntentConfirmHandle.walleeSelectedPaymentId
                );

                if (!config.preventPaymentProvider) {

                    this.resetCartIfPossible();
                    this.walleeHandler.submit();
                }
            } catch (ex) {
                logError(ex);
                // do nothing
            }
        }
    }

    public setPaymentMethod(method: IPaymentMethod, withCardToken?: ICreditCard): any {
        this.selectedPaymentMethod = method;
        this.selectedCreditCard = withCardToken;

        let returnHandler: any = undefined;

        if (method.provider === PaymentProvider.Wallee || method.provider === PaymentProvider.PostFinance) {
            this.stripeHandler = undefined;
            returnHandler = this.walleeHandler;
        } else if (method.provider === PaymentProvider.Stripe) {
            this.walleeHandler = undefined;
            returnHandler = (handler: { (): any }) => {
                this.stripeHandler = handler;
            };
        } else {
            this.walleeHandler = undefined;
            this.stripeHandler = undefined;
        }

        this.checkState(true);
        return returnHandler;
    }

    public reset() {
        this.state = UiPaymentState.Preparing;
        this.selectedPaymentMethod = undefined;
        this.selectedCreditCard = undefined;
        this.walleeHandler = undefined;
        this.stripeHandler = undefined;
        this.consumption = 0;
        this.total = 0;
        this.fees = 0;
        this.tipPercent = this.defaultTipPercent;
        this.tipAmount = 0;
        this.intent = undefined;
        this.paymentInProgress = false;
    }

    private checkState(forceDispatch?: boolean) {
        let newState = UiPaymentState.Preparing;
        const isPayLater = this.isPayLater();
        if (this.paymentInProgress) {
            if (isPayLater) {
                newState = UiPaymentState.Ordering;
            } else {
                newState = UiPaymentState.Paying;
            }
        } else {
            if (this.intent === undefined || this.total < 0) {
                newState = UiPaymentState.Preparing;
            } else if (
                this.selectedPaymentMethod === undefined ||
                (this.selectedPaymentMethod.provider === PaymentProvider.Stripe &&
                    this.selectedCreditCard === undefined &&
                    this.stripeHandler === undefined)
            ) {
                newState = UiPaymentState.MethodSelection;
            } else {
                newState = UiPaymentState.ReadyToPay;
            }
        }

        if (newState !== this.state || forceDispatch !== undefined) {
            this.state = newState;
            this.onStateChanged.dispatch();
        }
    }

    private isPayService(service: IService): boolean {
        return service.serviceType == ServiceType.Pay;
    }

    private getSocketConnectionId() {
        return undefined;
        // return TastierClient.context.socket.connectionId;
    }
}
